import type { ReactNode } from "react";
import { EvalPolicyTable } from "./EvalPolicyTable";
import {
  formatParamValue,
  paramValuesEqual,
  type EvalParamSpec,
  type EvalParamValues,
} from "./evalParamSpecs";
import type { EvaluationTargetPolicy } from "./evaluationTargetPolicy";
import styles from "./EvalStartSummary.module.css";

// 평가 시작이 보낼 내용을 표시한다: 이미지, run 수, 모든 파라미터. 바뀐 값을 먼저 표시한다.
// 클라이언트 쪽 스냅샷이라 조회하지 않는다. 두 곳이 함께 쓴다:
//   - 시작 확인 다이얼로그: 대상 policy 를 함께 표시한다.
//   - 학습 제출 위저드: 평가를 아직 없는 세션에 연결하므로 policy 가 없다.

export type EvalStartSummaryProps = {
  selectedTag: string;
  imageUri: string | null;
  specs: readonly EvalParamSpec[];
  values: EvalParamValues;
  // seed 계획의 policy 당 평가 수. seed 입력이 유효하지 않으면 null 이다.
  seedCount: number | null;
  // 시작에 대상 policy 가 이미 있을 때의 목록. policy 행을 추가하고 policy 당 run 수를 계산한다.
  // `nowMs` 는 호출부가 소유한 상대 시각 기준이다.
  policies?: { items: readonly EvaluationTargetPolicy[]; nowMs: number };
};

export function EvalStartSummary({
  selectedTag,
  imageUri,
  specs,
  values,
  seedCount,
  policies,
}: EvalStartSummaryProps): ReactNode {
  const entries = specs.map((spec) => {
    const value = values[spec.key] ?? spec.default;
    return { spec, value, changed: !paramValuesEqual(value, spec.default) };
  });
  const changedCount = entries.filter((entry) => entry.changed).length;
  // 안정 정렬이라 바뀐 절반과 바뀌지 않은 절반 안에서 spec 순서를 유지한다.
  const sortedEntries = [...entries].sort((a, b) => Number(b.changed) - Number(a.changed));
  const multiplePolicies = policies !== undefined && policies.items.length > 1;

  return (
    <div className={styles.summary}>
      <div className={styles.summaryCard}>
        {policies !== undefined ? (
          <>
            <span className={styles.summaryLabel}>Policy</span>
            <div className={styles.policyBlock}>
              <span className={styles.policyHeading}>
                {multiplePolicies
                  ? `선택한 Policy ${policies.items.length}개`
                  : policies.items[0]?.name}
              </span>
              <EvalPolicyTable policies={policies.items} nowMs={policies.nowMs} />
            </div>
          </>
        ) : null}
        <span className={styles.summaryLabel}>Image Tag</span>
        <span className={styles.monoValue}>{selectedTag}</span>
        <span className={styles.summaryLabel}>Image URI</span>
        <span className={styles.monoValueBreak}>{imageUri ?? "—"}</span>
        {seedCount !== null ? (
          <>
            <span className={styles.summaryLabel}>실행 횟수</span>
            <span>
              {policies !== undefined
                ? `Policy 당 Evaluation ${seedCount}회`
                : `Evaluation ${seedCount}회`}
            </span>
          </>
        ) : null}
      </div>

      <div className={styles.paramsHeader}>
        <h3 className={styles.paramsTitle}>파라미터</h3>
        <span className={styles.changedBadge}>
          {`${entries.length}개 중 ${changedCount}개 변경`}
        </span>
      </div>
      <div className={styles.tableWrap}>
        <table className={styles.paramsTable}>
          <thead>
            <tr>
              <th scope="col" className={styles.paramCol}>
                파라미터
              </th>
              <th scope="col" className={styles.valueCol}>
                기본값
              </th>
              <th scope="col" className={styles.valueCol}>
                현재 값
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedEntries.map(({ spec, value, changed }) => (
              <tr key={spec.key}>
                <th scope="row" className={styles.paramCell}>
                  <span className={styles.paramLabel}>{spec.label}</span>
                  <span className={styles.paramKey}>{spec.key}</span>
                  {spec.description != null ? (
                    <span className={styles.paramDescription}>{spec.description}</span>
                  ) : null}
                </th>
                <td className={styles.valueCell}>
                  <span className={styles.paramValue}>{formatParamValue(spec.default)}</span>
                </td>
                <td className={styles.valueCell}>
                  <span className={changed ? styles.paramValueChanged : styles.paramValue}>
                    {formatParamValue(value)}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
